import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Download, FileText, FolderOpen, Plus, Search, Trash2 } from "lucide-react";
import { apiRequest, downloadFile } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const DOCUMENT_CATEGORIES = [
  { value: "dvr", label: "DVR" },
  { value: "verbali_consultazione", label: "Verbali consultazione" },
  { value: "nomine", label: "Nomine" },
  { value: "formazione", label: "Formazione" },
  { value: "dpi", label: "DPI" },
  { value: "sorveglianza_sanitaria", label: "Sorveglianza sanitaria" },
  { value: "attrezzature", label: "Attrezzature" },
  { value: "sostanze", label: "Sostanze" },
  { value: "appalti_duvri", label: "Appalti / DUVRI" },
  { value: "planimetrie", label: "Planimetrie" },
  { value: "certificazioni", label: "Certificazioni" },
  { value: "contratti", label: "Contratti" },
  { value: "altro", label: "Altro" },
];

const categoryLabel = (key) => DOCUMENT_CATEGORIES.find((c) => c.value === key)?.label || key;

function formatBytes(size) {
  if (!size) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = size;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i += 1; }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[i]}`;
}

function CompanyPicker() {
  const [companies, setCompanies] = useState([]);
  useEffect(() => { apiRequest("/companies").then(setCompanies); }, []);
  return (
    <div className="space-y-6" data-testid="documents-picker-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Gestione Documenti</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">Seleziona azienda</h2>
        <p className="mt-2 text-slate-600">Apri il repository documentale di un&apos;azienda per caricare, scaricare e gestire i file.</p>
      </section>
      <Card className="shadow-none"><CardContent className="space-y-3 p-6">
        {companies.length === 0 && <p className="rounded-md border border-dashed border-slate-300 p-6 text-sm text-slate-500" data-testid="documents-picker-empty">Nessuna azienda disponibile.</p>}
        {companies.map((company) => (
          <Link key={company.id} to={`/aziende/${company.id}/documenti`} className="flex justify-between rounded-md border border-slate-200 p-4 transition hover:bg-slate-50" data-testid={`documents-company-${company.id}`}>
            <span className="font-semibold text-slate-950">{company.business_name}</span>
            <Badge className="bg-blue-50 text-blue-900">Apri</Badge>
          </Link>
        ))}
      </CardContent></Card>
    </div>
  );
}

const emptyForm = { document_name: "", category: "dvr", description: "", expiry_date: "", site_id: "", tags: "" };

export default function Documents() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [sites, setSites] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [filter, setFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const canWrite = ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN", "RSPP"].includes(user.role);

  const load = async () => {
    if (!id) return;
    const [companyData, sitesData, docsData] = await Promise.all([
      apiRequest(`/companies/${id}`),
      apiRequest(`/companies/${id}/sites`),
      apiRequest(`/companies/${id}/documents`),
    ]);
    setCompany(companyData);
    setSites(sitesData);
    setDocuments(docsData);
  };
  useEffect(() => { load().catch((err) => setError(err.message)); }, [id]);

  if (!id) return <CompanyPicker />;
  if (!company) return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="documents-loading">Caricamento documenti...</div>;

  const filtered = documents.filter((doc) => {
    if (categoryFilter && doc.category !== categoryFilter) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (doc.document_name || "").toLowerCase().includes(q) || (doc.description || "").toLowerCase().includes(q) || (doc.file_name || "").toLowerCase().includes(q);
  });

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!file) { setError("Seleziona un file da caricare"); return; }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      data.append("document_name", form.document_name);
      data.append("category", form.category);
      if (form.description) data.append("description", form.description);
      if (form.expiry_date) data.append("expiry_date", form.expiry_date);
      if (form.site_id) data.append("site_id", form.site_id);
      if (form.tags) data.append("tags", form.tags);
      await apiRequest(`/companies/${id}/documents`, { method: "POST", body: data });
      setForm(emptyForm); setFile(null);
      const input = document.getElementById("document-file-input");
      if (input) input.value = "";
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (docId) => {
    if (!window.confirm("Eliminare il documento? L'operazione non è reversibile.")) return;
    setError("");
    try { await apiRequest(`/companies/${id}/documents/${docId}`, { method: "DELETE" }); await load(); }
    catch (err) { setError(err.message); }
  };

  const download = async (doc) => {
    setError("");
    try { await downloadFile(`/companies/${id}/documents/${doc.id}/download`, doc.file_name || doc.document_name); }
    catch (err) { setError(err.message); }
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(`/aziende/${id}`)} data-testid="documents-back-button"><ArrowLeft className="h-4 w-4" /> Scheda azienda</Button>
        <Badge className="bg-blue-50 text-blue-900" data-testid="documents-company-badge">{company.business_name}</Badge>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Repository documentale</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">Gestione Documenti</h2>
        <p className="mt-2 text-slate-600">Carica e organizza tutti i documenti dell&apos;azienda: DVR, nomine, attestati, verbali, planimetrie, contratti e altro. I file restano collegati alla pratica DVR.</p>
      </section>

      {error && <Alert variant="destructive" data-testid="documents-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}

      <section className="grid gap-4 md:grid-cols-3" data-testid="documents-stats">
        <Card className="shadow-none"><CardContent className="p-4"><p className="text-sm text-slate-500">Documenti totali</p><p className="font-heading text-2xl font-bold text-slate-950" data-testid="documents-count-total">{documents.length}</p></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4"><p className="text-sm text-slate-500">Scaduti</p><p className="font-heading text-2xl font-bold text-red-700" data-testid="documents-count-expired">{documents.filter((d) => d.is_expired).length}</p></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4"><p className="text-sm text-slate-500">Categorie usate</p><p className="font-heading text-2xl font-bold text-blue-900" data-testid="documents-count-categories">{new Set(documents.map((d) => d.category)).size}</p></CardContent></Card>
      </section>

      {canWrite && (
        <Card className="shadow-none" data-testid="documents-upload-card">
          <CardHeader><CardTitle className="font-heading">Carica nuovo documento</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid gap-3 md:grid-cols-2" data-testid="documents-upload-form">
              <div className="space-y-2"><Label>Nome documento</Label><Input value={form.document_name} onChange={(e) => setForm({ ...form, document_name: e.target.value })} required minLength={2} placeholder="Es. DVR 2026 - rev. 1" data-testid="document-name-input" /></div>
              <div className="space-y-2"><Label>Categoria</Label><select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="document-category-select">{DOCUMENT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select></div>
              <div className="space-y-2 md:col-span-2"><Label>Descrizione</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Note sintetiche sul documento" data-testid="document-description-input" /></div>
              <div className="space-y-2"><Label>Sede collegata (opzionale)</Label><select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} data-testid="document-site-select"><option value="">Nessuna sede</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.site_name}</option>)}</select></div>
              <div className="space-y-2"><Label>Data di scadenza (opzionale)</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="document-expiry-input" /></div>
              <div className="space-y-2 md:col-span-2"><Label>Tag (separati da virgola)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Es. 2026, rev1, urgente" data-testid="document-tags-input" /></div>
              <div className="space-y-2 md:col-span-2"><Label>File (max 25 MB)</Label><Input id="document-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} required data-testid="document-file-input" /></div>
              <Button className="bg-blue-900 hover:bg-blue-800 md:col-span-2" disabled={uploading} data-testid="document-submit-button"><Plus className="h-4 w-4" /> {uploading ? "Caricamento..." : "Carica documento"}</Button>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="shadow-none" data-testid="documents-list-card">
        <CardHeader>
          <CardTitle className="font-heading">Documenti caricati</CardTitle>
          <div className="mt-4 grid gap-3 md:grid-cols-3" data-testid="documents-filters">
            <div className="relative"><Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-slate-400" /><Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Cerca per nome, descrizione, file..." className="pl-8" data-testid="documents-search-input" /></div>
            <select className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="documents-category-filter">
              <option value="">Tutte le categorie</option>
              {DOCUMENT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <p className="self-center text-sm text-slate-500" data-testid="documents-filtered-count">{filtered.length} risultati</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {filtered.length === 0 && <p className="rounded-md border border-dashed border-slate-300 p-6 text-sm text-slate-500" data-testid="documents-empty">Nessun documento corrispondente.</p>}
          {filtered.map((doc) => (
            <div key={doc.id} className="flex flex-col gap-3 rounded-md border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`document-row-${doc.id}`}>
              <div className="flex items-start gap-3">
                <FileText className="mt-1 h-5 w-5 text-blue-900" />
                <div>
                  <p className="font-semibold text-slate-950" data-testid={`document-name-${doc.id}`}>{doc.document_name}</p>
                  <p className="text-sm text-slate-500" data-testid={`document-meta-${doc.id}`}>{doc.file_name} · {formatBytes(doc.file_size)} · caricato da {doc.uploaded_by_name || "—"} il {new Date(doc.created_at).toLocaleDateString("it-IT")}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <Badge className="bg-slate-100 text-slate-700" data-testid={`document-category-${doc.id}`}>{categoryLabel(doc.category)}</Badge>
                    {doc.expiry_date && <Badge className={doc.is_expired ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"} data-testid={`document-expiry-${doc.id}`}>Scadenza {doc.expiry_date}{doc.is_expired ? " (scaduta)" : ""}</Badge>}
                    {(doc.tags || []).map((tag) => <Badge key={tag} className="bg-blue-50 text-blue-900">{tag}</Badge>)}
                  </div>
                  {doc.description && <p className="mt-2 text-sm text-slate-600">{doc.description}</p>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => download(doc)} data-testid={`download-document-${doc.id}`}><Download className="h-4 w-4" /> Scarica</Button>
                {canWrite && <Button variant="outline" size="sm" className="text-red-700" onClick={() => remove(doc.id)} data-testid={`delete-document-${doc.id}`}><Trash2 className="h-4 w-4" /> Elimina</Button>}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
